import { useMemo, useState } from "react";
import SearchBox from "./SearchBox";
import StatusBadge from "./StatusBadge";
import ActionMenu from "./ActionMenu";
import Pagination from "./Pagination";
import { ArrowUpDown, Download, Printer } from "lucide-react";

export default function DataTable({
  title,
  columns,
  data,
  onView,
  onEdit,
  onDelete,
}) {
  const [perPage, setPerPage] = useState(5);
  
  // 👇 FIX: Default sorting ko 'id' aur 'desc' kar diya hai taaki latest record hamesha upar aaye!
  const [sortField, setSortField] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // 1. Search/Filter MUST come first
  const filteredData = useMemo(() => {
    return data.filter((item) =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [data, search]);

  // 2. Sort the already filtered data
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      if (!sortField) return 0;

      const valueA = a[sortField];
      const valueB = b[sortField];

      if (valueA < valueB) {
        return sortOrder === "asc" ? -1 : 1;
      }
      if (valueA > valueB) {
        return sortOrder === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortField, sortOrder]);

  // 3. Paginate
  const totalPages = Math.max(1, Math.ceil(filteredData.length / perPage));

  const paginatedData = sortedData.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  // --- PREMIUM FEATURES START ---

  // Export to CSV Logic
  const handleExportCSV = () => {
    if (sortedData.length === 0) return;
    
    // Get headers
    const headers = columns.map(col => col.label).join(",");
    
    // Map rows matching the column keys
    const csvRows = sortedData.map(row => {
      return columns.map(col => {
        let cellData = row[col.key] || "";
        // Wrap in quotes to handle commas inside the data
        return `"${String(cellData).replace(/"/g, '""')}"`; 
      }).join(",");
    });

    const csvString = [headers, ...csvRows].join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    
    const a = document.createElement("a");
    a.setAttribute("href", url);
    a.setAttribute("download", `${title ? title.replace(/\s+/g, "_") : "Data_Export"}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Print Logic
  const handlePrint = () => {
    window.print();
  };

  // --- PREMIUM FEATURES END ---

  return (
    <div className="bg-white rounded-2xl shadow border border-gray-200">
      
      {/* Top Bar: Title, Search, and Per Page Select */}
      <div className="flex flex-col md:flex-row justify-between items-center p-6 border-b border-gray-100 gap-4">
        {title && <h2 className="text-xl font-semibold">{title}</h2>}
        
        <div className="flex flex-wrap items-center gap-3">
          <SearchBox
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />

          {/* New Action Buttons */}
          <button 
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition print:hidden"
            title="Export to CSV"
          >
            <Download size={16} /> <span className="hidden sm:inline">Export</span>
          </button>

          <button 
            onClick={handlePrint}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition print:hidden"
            title="Print Data"
          >
            <Printer size={16} /> <span className="hidden sm:inline">Print</span>
          </button>

          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-yellow-500 print:hidden"
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </select>
        </div>
      </div>

      {/* Showing entries info */}
      <div className="px-6 pt-5 pb-2 print:hidden">
        <p className="text-sm text-gray-500">
          Showing{" "}
          {filteredData.length === 0 ? 0 : (currentPage - 1) * perPage + 1} -{" "}
          {Math.min(currentPage * perPage, filteredData.length)} of{" "}
          {filteredData.length} entries
        </p>
      </div>

      {/* Table */}
      <div className="overflow-x-auto print:m-0 print:border-none print:shadow-none">
        <table className="min-w-full">
          <thead className="bg-gray-100 print:bg-transparent">
            <tr>
              {columns.main ? columns.map((column) => ( // Safe check or standard map
                <th
                  key={column.key}
                  onClick={() => {
                    if (sortField === column.key) {
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                    } else {
                      setSortField(column.key);
                      setSortOrder("asc");
                    }
                  }}
                  className="px-6 py-4 text-left font-semibold cursor-pointer hover:bg-gray-200 transition-colors print:text-black"
                >
                  <div className="flex items-center gap-2">
                    {column.label}
                    <ArrowUpDown size={15} className="text-gray-400 print:hidden" />
                  </div>
                </th>
              )) : columns.map((column) => (
                <th
                  key={column.key}
                  onClick={() => {
                    if (sortField === column.key) {
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                    } else {
                      setSortField(column.key);
                      setSortOrder("asc");
                    }
                  }}
                  className="px-6 py-4 text-left font-semibold cursor-pointer hover:bg-gray-200 transition-colors print:text-black"
                >
                  <div className="flex items-center gap-2">
                    {column.label}
                    <ArrowUpDown size={15} className="text-gray-400 print:hidden" />
                  </div>
                </th>
              ))}
              <th className="px-6 py-4 text-center font-semibold print:hidden">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="text-center py-10 text-gray-500"
                >
                  No records found.
                </td>
              </tr>
            ) : (
              paginatedData.map((row) => (
                <tr
                  key={row.id}
                  className="border-b hover:bg-gray-50 transition print:break-inside-avoid"
                >
                  {columns.map((column) => (
                    <td key={column.key} className="px-6 py-4 print:text-black">
                      {column.key === "status" ? (
                        <StatusBadge status={row.status} />
                      ) : column.format ? (
                        column.format(row[column.key])
                      ) : (
                        row[column.key]
                      )}
                    </td>
                  ))}
                  <td className="px-6 py-4 print:hidden">
                    <ActionMenu
                      onView={onView ? () => onView(row) : undefined}
                      onEdit={onEdit ? () => onEdit(row) : undefined}
                      onDelete={onDelete ? () => onDelete(row) : undefined}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="p-6 print:hidden">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
}